// 지출 유형별 템플릿.
// ⚠️ 지금은 JS 상수지만, 다음 스텝에서 DB(ExpenseTemplate 모델)로 그대로 옮긴다.
//    여기서 유형을 늘려도 미리보기/폼 코드는 한 줄도 안 바뀐다 — "데이터"만 늘어난다.
//
// 필드 타입:
//   text   : 라벨 : 값
//   money  : 라벨 : 금1,824,552원(금일백팔십이만...원)   ← 한글 자동 변환
//   table  : 라벨 : + 아래 표 (columns 헤더 + values.rows)
//
// intro 는 문자열 또는 (values) => 문자열.

// 문자열 끝의 한글 음절을 찾아 받침 유무로 "을"/"를"을 판정.
// 괄호·영문·숫자로 끝나도(예: "복사용지(A4)") 뒤에서부터 훑어 마지막 한글 음절을 기준으로 삼는다.
function getEulReul(word) {
  if (!word) return "을(를)";
  const chars = [...word];
  for (let i = chars.length - 1; i >= 0; i--) {
    const code = chars[i].charCodeAt(0);
    if (code >= 0xac00 && code <= 0xd7a3) {
      const hasBatchim = (code - 0xac00) % 28 !== 0;
      return hasBatchim ? "을" : "를";
    }
  }
  return "을(를)"; // 한글 음절이 없으면 안전하게 병기
}

export const EXPENSE_TEMPLATES = {
  credit_card: {
    code: "credit_card",
    name: "신용카드 결제대금 납부",
    intro: (v) =>
      `금천일자리주식회사 신용카드(${v.cardNo || ""})의 ${v.month || ""} 결제대금을 납부하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      { key: "vendor", label: "업  체 명", type: "text" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내      역", "일시불", "연회비", "총      계", "비      고"],
      },
    ],
    attachments: [{ name: "우리카드 이용대금명세서", count: 1 }],
  },

  general_purchase: {
    code: "general_purchase",
    name: "물품구매",
    intro: (v) =>
      `금천일자리주식회사 ${v.item || ""}${getEulReul(v.item)} 아래와 같이 구매하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "vendor", label: "구매업체", type: "text" },
      { key: "method", label: "구매방법", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 사무관리비,\n일반수용비, 사무용품비",
      },
    ],
    attachments: [{ name: "견적서", count: 1 }],
    defaultMethod: "신용카드결제",
  },

  meeting_expense: {
    code: "meeting_expense",
    name: "간담회비",
    intro:
      "금천일자리주식회사 사업점검 및 업무협의를 위해 간담회를 진행하고 아래와 같이 지출하여 보고드립니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "datetime", label: "일      시", type: "text", input: true },
      { key: "place", label: "장      소", type: "text", input: true },
      { key: "target", label: "집행대상", type: "text", input: true },
      { key: "method", label: "지출방법", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 일반운영비,\n업무추진비, 사업업무추진비",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
    defaultMethod: "체크카드결제",
  },

  accounting_fee: {
    code: "accounting_fee",
    name: "기장수수료",
    intro: (v) =>
      `금천일자리주식회사 ${v.month || ""} 기장수수료를 아래와 같이 지출하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "vendor",
        label: "업      체",
        type: "text",
        input: true,
        default: "세무법인 이레(대표세무사:김상길)",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체(우리은행 1005-701-807677/세무법인이레)",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 일반운영비,\n사무관리비, 일반수용비, 지급수수료, 세무회계 관련 수수료",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }],
  },

  utility_fee: {
    code: "utility_fee",
    name: "인터넷·전화요금",
    intro: (v) =>
      `금천일자리주식회사 사무실 ${v.month || ""} 인터넷 및 전화요금을 아래와 같이 납부하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "전용지정계좌이체(우리은행 296051-88-018223)",
      },
      // ✅ input:true 없음 — 폼에서는 TemplateFormFields.jsx의 DueDateFields(연/월/일 분리
      // 입력)가 templateInputs.dueDate를 직접 채운다. 여기 정의는 PDF 미리보기 라벨("4. 납 기 일")용.
      { key: "dueDate", label: "납  기  일", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와관리비, 일반운영비, 공공운영비,\n공공요금 및 제세, KT전화요금(인터넷)",
      },
    ],
    attachments: [{ name: "청구서", count: 1 }],
  },

  parking_fee: {
    code: "parking_fee",
    name: "주차비용",
    intro: (v) =>
      `${v.branch || ""} ${v.reason || ""}에 따라 발생한 주차비용을 아래와 같이 지출하여 보고드립니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "vendor",
        label: "지출업체",
        type: "text",
        input: true,
        default: "금천구청",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n사무관리비, 일반수용비",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
  },

  corporate_seal: {
    code: "corporate_seal",
    name: "법인인감증명서",
    intro: (v) =>
      `${v.reason || ""}에 필요한 법인인감증명서 발급 비용을 아래와 같이 지출하여 보고드립니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        multiline: true,
        input: true,
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n사무관리비, 일반수용비",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
  },

  fund_transfer: {
    code: "fund_transfer",
    name: "보전일시자금",
    intro: (v) =>
      `금천일자리주식회사 지점인 '${v.fullBranch || ""}' 신규 설립에 따른 초기 물품 구입 자금 확보를 위하여 본사 용역수익금 통장에서 ${v.fullBranch || ""} 통장으로 보전일시자금을 이체하고자 아래와 같이 처리하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "이체금액", type: "money" },
      {
        key: "detail",
        label: "처리내용",
        type: "text",
        multiline: true,
        input: true,
      },
      {
        key: "method",
        label: "이체방법",
        type: "text",
        input: true,
      },
    ],
    attachments: [{ name: "예수금통장 이체확인증", count: 1 }],
  },

  biz_license: {
    code: "biz_license",
    name: "사업자등록증",
    intro: (v) =>
      `${v.target || ""} 운영 준비와 관련하여 사업자등록증 발급에 따른 비용을 아래와 같이 지출하여 보고드립니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      { key: "vendor", label: "업      체", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n사무관리비, 일반수용비",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
  },

  biz_report: {
    code: "biz_report",
    name: "영업신고수수료",
    intro: (v) =>
      `${v.target || ""} 개점 준비와 관련하여 ${v.bizCategory || ""} 영업신고에 따른 수수료를 아래와 같이 지출하여 보고드립니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      { key: "vendor", label: "업      체", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n사무관리비, 일반수용비",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
  },

  supplies: {
    code: "supplies",
    name: "소모품구매",
    intro: (v) =>
      `금천일자리주식회사 소모품(${v.item || ""})을 아래와 같이 구매하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "vendor", label: "구매업체", type: "text" },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임 거래명세표 참고",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n사무관리비, 일반수용비, 소모품비",
      },
    ],
    attachments: [
      { name: "거래명세표", count: 1 },
      { name: "카드매출전표", count: 1 },
    ],
  },

  supplies_office: {
    code: "supplies_office",
    name: "소모(관서업무비)",
    intro: (v) =>
      `금천일자리주식회사 소모품(${v.item || ""})을 아래와 같이 구매하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "vendor", label: "구매업체", type: "text" },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임. 거래명세표 참고",
      },
      {
        key: "method",
        label: "구매방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 관서업무비,\n부서업무비, 경영지원팀 부서운영업무비",
      },
    ],
    attachments: [
      { name: "거래명세표", count: 1 },
      { name: "현금영수증", count: 2 },
    ],
  },

  payroll: {
    code: "payroll",
    name: "임직원급여",
    intro: (v) => `${v.month || ""} 임직원 급여를 다음과 같이 지급하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      { key: "payDate", label: "지  급  일", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지정계좌이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임 급여내역서 참조",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "본사, 사업비용, 영업비용, 판매비와일반관리비, 인건비, 보수",
      },
    ],
    attachments: [{ name: "임직원 급여내역서", count: 1 }],
  },

  printer_rental: {
    code: "printer_rental",
    name: "복합기렌탈료",
    intro: "금천일자리주식회사 사무실 복합기 렌탈료를 아래와 같이 지출하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요금액", type: "money" },
      {
        key: "vendor",
        label: "업      체",
        type: "text",
        input: true,
        default: "자이온솔루션주식회사",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체(기업은행 596-017294-01-010)",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비,\n사무관리비, 임차료, 지급임차료, 복합기 렌탈료",
      },
    ],
    attachments: [
      { name: "거래명세표", count: 1 },
      { name: "세금계산서", count: 1 },
    ],
  },

  office_maintenance: {
    code: "office_maintenance",
    name: "사무실관리비",
    intro: (v) =>
      `금천일자리주식회사 사무실 ${v.month || ""}분 관리비를 아래와 같이 납부하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지정계좌이체(우리은행 1005-001-334091)",
      },
      { key: "dueDate", label: "납  기  일", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n사무관리비, 임차료, 지급임차료, 사무실 관리비",
      },
    ],
    attachments: [
      { name: "관리비 명세서", count: 1 },
      { name: "관리비 부과 내역서", count: 1 },
      { name: "계산서", count: 1 },
      { name: "세금계산서", count: 1 },
    ],
  },

  donation: {
    code: "donation",
    name: "기부금",
    intro: (v) =>
      `${v.reason || ""}, ${v.target || ""}에 다음과 같이 기부를 진행하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "vendor", label: "업      체", type: "text" },
      { key: "method", label: "지출방법", type: "text", input: true },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "본사, 사업비용, 영업외비용, 기타영업외비용, 기부금",
      },
    ],
    attachments: [
      { name: "고유번호증", count: 1 },
      { name: "통장사본", count: 1 },
    ],
  },

  postage: {
    code: "postage",
    name: "우편발송",
    intro: (v) =>
      `금천일자리주식회사 ${v.item || ""}${getEulReul(v.item)} 우편 발송하여 보고드립니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 공공운영비,\n공공요금 및 제세, 우편물발송 및 문자발송료",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
  },

  welfare_points: {
    code: "welfare_points",
    name: "복지포인트",
    intro:
      "금천일자리주식회사 직원 맞춤형 복지비용(복지포인트)을 아래와 같이 구매하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "신청금액", type: "money" },
      { key: "applicant", label: "신 청 인", type: "text" },
      { key: "applyReason", label: "신청사유", type: "text" },
      { key: "method", label: "지급방법", type: "text", input: true },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 일반운영비, 복리후생비, 복지포인트",
      },
    ],
    attachments: [
      { name: "맞춤형 복지비용 신청서", count: 1 },
      { name: "영수증", count: 1 },
    ],
  },

  service_contract: {
    code: "service_contract",
    name: "계약인지세",
    intro: (v) =>
      v.category === "환경/종합"
        ? `${v.contractName || ""} 용역 계약을 아래와 같이 체결하고자 합니다.`
        : `${v.contractName || ""}${getEulReul(v.contractName)} 아래와 같이 체결하고자 합니다.`,
    fields: [
      { key: "contractName", label: "계 약 명", type: "text" },
      { key: "contractAmount", label: "계약금액", type: "money" },
      { key: "period", label: "용역기간", type: "text" },
      { key: "client", label: "발 주 처", type: "text", input: true },
      { key: "cost", label: "소요비용", type: "text" },
      {
        key: "method",
        label: "계약방법",
        type: "text",
        multiline: true,
        input: true,
        default:
          "수의계약\n- 근거법령 : 지방자치단체를 당사자로 하는 계약에 관한 법률 시행령\n  제25조 제1항 제8호 사목",
      },
    ],
    attachments: [
      { name: "용역계약서(초안)", count: 1 },
      { name: "과업지시서", count: 1 },
      { name: "산출내역서", count: 1 },
    ],
  },

  job_training: {
    code: "job_training",
    name: "직무교육비",
    intro: (v) =>
      v.trainee
        ? `금천일자리주식회사 ${v.trainee} 직무 교육비를 아래와 같이 지출하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "course", label: "교육과정", type: "text" },
      { key: "schedule", label: "교육일정", type: "text" },
      { key: "target", label: "대 상 자", type: "text" },
      { key: "institution", label: "교육기관", type: "text" },
      { key: "method", label: "지출방법", type: "text", input: true },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n사무관리비, 교육훈련비",
      },
    ],
    attachments: [
      { name: "강의계획서", count: 1 },
      { name: "입금요청서", count: 1 },
    ],
  },

  banner_ad: {
    code: "banner_ad",
    name: "배너광고",
    intro: (v) =>
      v.adTarget
        ? `금천일자리주식회사 ${v.adTarget} 온라인 배너 광고를 진행하고 아래와 같이 지출하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "vendor", label: "업 체 명", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 광고선전비,\n온라인 사업홍보",
      },
    ],
    attachments: [
      { name: "청구서", count: 1 },
      { name: "세금계산서", count: 1 },
    ],
  },

  electric_bill: {
    code: "electric_bill",
    name: "전기료",
    intro: (v) =>
      v.month
        ? `금천일자리주식회사 사무실 ${v.month}분 전기료를 아래와 같이 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지로납부_전용지정계좌이체(우리은행 00230730118338)",
      },
      { key: "usagePeriod", label: "사용기간", type: "text" },
      { key: "dueDate", label: "납  기  일", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 공공운영비,\n공공요금 및 제세, 전기료",
      },
    ],
    attachments: [{ name: "전기요금 청구서", count: 1 }],
  },

  fuel_cost: {
    code: "fuel_cost",
    name: "유류비",
    intro: (v) =>
      v.carModel
        ? `금천일자리주식회사 업무용 차량 ${v.carNumber || ""}(${v.carModel}) 유류비${v.washIncluded ? " 및 세차비" : ""}를 지출하여 보고드립니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        placeholder: "예: 1,977원*23.268ℓ (세차 등 동시 결제 시 병기)",
      },
      { key: "vendor", label: "업 체 명", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n공공운영비, 차량선박비",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
  },

  // ✅ 용역사업 전용 — 물품구매 (표 형식 세부내역)
  service_purchase: {
    code: "service_purchase",
    name: "물품구매",
    intro: (v) =>
      v.target
        ? `${v.target}에게 제공할 ${v.item || ""}${getEulReul(v.item)} 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["상품명", "수량", "단가", "할인", "합계", "배송비", "소계", "비고"],
      },
      { key: "vendor", label: "구매업체", type: "text" },
      { key: "method", label: "구매방법", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "관리사업수입,사업비용,영업비용,판매비와일반관리비,물건비",
      },
    ],
    attachments: [{ name: "견적서", count: 1 }],
  },

  // ✅ 용역사업 전용 — 위탁수수료
  // ✅ 용역사업 전용 — 위탁수수료(보건). 매달 반복되는 정형 기안이라 문구를 고정하고
  // 귀속연월만 입력하면 되도록 만든다 (범용 위탁수수료 템플릿을 대체).
  service_commission_health: {
    code: "service_commission_health",
    name: "수수료(보건)",
    intro: (v) =>
      v.month
        ? `${v.month} 소중한 산업보건연구소(주)와 체결한 보건관리 위탁 운영을 위하여 아래와 같이 결제하여 현장근무자의 건강 관리에 만전을 기하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임 참조",
      },
      { key: "vendor", label: "위탁업체", type: "text" },
      {
        key: "method",
        label: "결제방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "본사, 사업비용, 영업비용, 판매비와 일반관리비, 일반운영비, 사무관리비 기타수용비",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }],
  },

  // ✅ 용역사업 전용 — 위탁수수료(안전). 보건과 문구/위탁업체만 다르고 구조는 동일.
  service_commission_safety: {
    code: "service_commission_safety",
    name: "수수료(안전)",
    intro: (v) =>
      v.month
        ? `${v.month} (사)대한산업안전협회 서울지역본부와 체결한 산업안전관리업무 위탁 운영을 위하여 아래와 같이 결제하여 금천일자리주식회사 근무자들의 안전 관리에 만전을 기하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임 참조",
      },
      { key: "vendor", label: "위탁업체", type: "text" },
      {
        key: "method",
        label: "결제방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "본사, 사업비용, 영업비용, 판매비와 일반관리비, 일반운영비, 사무관리비 기타수용비",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }],
  },

  // ✅ 용역사업 전용 — 일용직급여 (표 형식 세부내역, 지급 합계를 소요예산으로)
  service_daily_wage: {
    code: "service_daily_wage",
    name: "일용직급여",
    intro: (v) =>
      v.month ? `${v.month} 일용직 현장근로자 급여를 붙임과 같이 지급하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["근무자", "근무지", "근무일자", "지급", "공제", "실지급"],
      },
      { key: "method", label: "지급방법", type: "text", input: true, default: "계좌이체" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "관리사업수익,사업비용,영업비용,판매비와일반관리비,인건비,기간제근로자등보수,급여",
      },
    ],
    attachments: [],
  },

  // ✅ 용역사업 전용(통합 페이지) — 용역대금 지급. 실데이터(176건 중 149건, 85%)의
  // 지배적 패턴이 "사업장별 재료비/복리후생비/용역비/위탁 물품·경비 구입"이라 물품구매
  // 계열(general_purchase/env_total_supplies)과 동일하게 품목표(ExpenseItemPanel)로
  // 설계 — target(사업장)만 용역 특유 필드로 추가.
  service_contract_payment: {
    code: "service_contract_payment",
    name: "용역대금 지급",
    intro: (v) =>
      v.target ? `${v.target} 용역사업 운영을 위해 아래와 같이 지출하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "target", label: "사 업 장", type: "text", input: true },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["품목명", "단위", "수량", "단가", "공급가액", "부가세", "비고"],
      },
      { key: "vendor", label: "구매업체", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "본사, 사업비용, 영업비용, 판매비와 일반관리비, 일반운영비, 사무관리비 기타수용비",
      },
    ],
    attachments: [{ name: "견적서", count: 1 }, { name: "세금계산서", count: 1 }],
  },

  // ✅ 용역사업 전용(통합 페이지) — 일용직급여. env_total_daily_wage와 동일 구조
  // (SalaryPanelIntegrated 입력을 그대로 반영) — 근무지/근무일자 컬럼이 있는 기존
  // service_daily_wage와 달리 SalaryPanel 표준 데이터 형태(이름/총지급액/공제내역/
  // 실수령액)를 쓴다.
  service_daily_payroll: {
    code: "service_daily_payroll",
    name: "일용직급여",
    intro: (v) =>
      v.month ? `${v.month} 일용직 현장근로자 급여를 붙임과 같이 지급하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["이름", "총지급액", "소득세", "지방세", "건강보험", "장기요양", "국민연금", "고용보험", "실수령액"],
      },
      { key: "method", label: "지급방법", type: "text", input: true, default: "계좌이체" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "관리사업수익, 사업비용, 영업비용, 판매비와일반관리비, 인건비, 기간제근로자등보수, 급여",
      },
    ],
    attachments: [],
  },

  // ✅ 환경/종합 전용 — 차량리스료. 매달 반복되는 정형 기안이라 값을 고정하고
  // 수납회차만 입력하면 되도록 만든다.
  env_total_car_lease: {
    code: "env_total_car_lease",
    name: "차량리스료",
    intro:
      "금천일자리주식회사 업무용 포터 차량(821로3826) 리스료를 아래와 같이 지출하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "vendor",
        label: "업      체",
        type: "text",
        input: true,
        default: "BNK캐피탈(리스담당자 남기호)",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "자동이체(부산은행 693-0279-0671-63)",
      },
      {
        key: "contractInfo",
        label: "계약정보",
        type: "table",
        showUnit: false,
        infoTable: true,
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비, 사무관리비, 임차료, 차량임차료",
      },
    ],
    attachments: [{ name: "납입안내장", count: 1 }],
  },

  // ✅ 환경/종합 전용(통합 페이지 파일럿) — 소모품구매. 경영팀 supplies 복제 +
  // 세부내역은 ExpenseItemPanel 입력을 그대로 표로 반영(원본은 "붙임 참고" 텍스트).
  env_total_supplies: {
    code: "env_total_supplies",
    name: "소모품구매",
    intro: (v) =>
      `금천일자리주식회사 소모품(${v.item || ""})을 아래와 같이 구매하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "vendor", label: "구매업체", type: "text" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["품목명", "단위", "수량", "단가", "공급가액", "부가세", "비고"],
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "환경사업, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n공공운영비, 소모품비",
      },
    ],
    attachments: [
      { name: "거래명세표", count: 1 },
      { name: "카드매출전표", count: 1 },
    ],
  },

  // ✅ 환경/종합 전용(통합 페이지 파일럿) — 임직원급여. 경영팀 payroll 그대로 복제.
  env_total_payroll: {
    code: "env_total_payroll",
    name: "임직원급여",
    intro: (v) => `${v.month || ""} 임직원 급여를 다음과 같이 지급하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      { key: "payDate", label: "지  급  일", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지정계좌이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임 급여내역서 참조",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "환경사업, 사업비용, 영업비용, 판매비와일반관리비, 인건비, 보수",
      },
    ],
    attachments: [{ name: "임직원 급여내역서", count: 1 }],
  },

  // ✅ 환경/종합 전용(통합 페이지 파일럿) — 일용직급여. 용역사업 service_daily_wage
  // 구조를 참고했으나, 세부내역 표는 SalaryPanel 입력(이름/총지급액/공제내역/실수령액)을
  // 그대로 반영한다 — 원본의 근무지/근무일자 컬럼은 SalaryPanel에 없어 제외(추후 필요시
  // SalaryPanel/EnvTotalExpenseSalary 확장 검토).
  env_total_daily_wage: {
    code: "env_total_daily_wage",
    name: "일용직급여",
    intro: (v) =>
      v.month ? `${v.month} 일용직 현장근로자 급여를 붙임과 같이 지급하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["이름", "총지급액", "소득세", "지방세", "건강보험", "장기요양", "국민연금", "고용보험", "실수령액"],
      },
      { key: "method", label: "지급방법", type: "text", input: true, default: "계좌이체" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "환경사업, 사업비용, 영업비용, 판매비와일반관리비, 인건비, 기간제근로자등보수, 급여",
      },
    ],
    attachments: [],
  },

  // ✅ 환경/종합 전용(통합 페이지 파일럿) — 직무교육비. 경영팀 job_training 그대로 복제.
  env_total_job_training: {
    code: "env_total_job_training",
    name: "직무교육비",
    intro: (v) =>
      v.trainee
        ? `금천일자리주식회사 ${v.trainee} 직무 교육비를 아래와 같이 지출하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "course", label: "교육과정", type: "text" },
      { key: "schedule", label: "교육일정", type: "text" },
      { key: "target", label: "대 상 자", type: "text" },
      { key: "institution", label: "교육기관", type: "text" },
      { key: "method", label: "지출방법", type: "text", input: true },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "환경사업, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n사무관리비, 교육훈련비",
      },
    ],
    attachments: [
      { name: "강의계획서", count: 1 },
      { name: "입금요청서", count: 1 },
    ],
  },

  // ✅ 환경/종합 전용(통합 페이지 파일럿, 신규) — 하도급 용역대금. 냉난방기 종합세척
  // 등 외주 용역 대금 지급용. 붙임 구성은 임시안 — 실물 기안과 대조해 확정 필요.
  env_total_subcontract: {
    code: "env_total_subcontract",
    name: "하도급 용역대금",
    intro: (v) =>
      v.vendor ? `${v.vendor} 용역 대금을 아래와 같이 지급하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지급금액", type: "money" },
      { key: "vendor", label: "업      체", type: "text" },
      {
        key: "detail",
        label: "용역내용",
        type: "text",
        input: true,
        default: "냉난방기 종합세척 용역 대금",
      },
      {
        key: "method",
        label: "지급방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "환경사업, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n공공운영비, 용역비",
      },
    ],
    attachments: [
      { name: "작업완료확인서", count: 1 },
      { name: "세금계산서", count: 1 },
    ],
  },

  // ✅ 환경/종합 전용(통합 페이지 파일럿, 신규) — 장비임차료. 냉난방기 세척 장비/
  // 입주청소 장비 등 임차 비용. printer_rental/water_rental 구조 참고. 붙임 구성은 임시안.
  env_total_equipment_rental: {
    code: "env_total_equipment_rental",
    name: "장비임차료",
    intro: (v) =>
      v.vendor
        ? `금천일자리주식회사 ${v.vendor} 임차 장비 사용료를 아래와 같이 지출하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요금액", type: "money" },
      { key: "vendor", label: "업      체", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "환경사업, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n사무관리비, 임차료, 장비임차료",
      },
    ],
    attachments: [
      { name: "거래명세표", count: 1 },
      { name: "세금계산서", count: 1 },
    ],
  },

  // ✅ 환경/종합 전용(통합 페이지 파일럿) — 끝. 이하 환경/소독 전용(전 사업 확산 [2]).
  // DB 실측(27건) 근거: 유류비 9건은 기존 fuel_cost 그대로 재사용(범용 템플릿,
  // env_total도 동일하게 재사용 중), 계약인지세 2건은 기존 service_contract 그대로
  // 재사용(intro가 category!=="환경/종합"이면 이미 일반 문구로 분기하므로 그대로 맞음).
  // 아래 3종만 소독 전용 신규.

  // ✅ 환경/소독 전용 — 임직원급여. env_total_payroll과 동일 구조로 복제(budgetItem만
  // "환경사업" 어투 유지, 실사용 4건: "급여/강미희/지급").
  env_disinfection_payroll: {
    code: "env_disinfection_payroll",
    name: "임직원급여",
    intro: (v) => `${v.month || ""} 임직원 급여를 다음과 같이 지급하고자 합니다.`,
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      { key: "payDate", label: "지  급  일", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지정계좌이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임 급여내역서 참조",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "환경사업, 사업비용, 영업비용, 판매비와일반관리비, 인건비, 보수",
      },
    ],
    attachments: [{ name: "임직원 급여내역서", count: 1 }],
  },

  // ✅ 환경/소독 전용(신규, 임시안) — 수수료 정산. 실사용 4건 전부 "(정산 용도)수수료
  // 합계"라는 동일 문구로 구분/내용/거래처가 모두 채워져 있어(내부 정산 성격의 특수
  // 패턴으로 보임) 붙임 구성은 실물과 대조 필요.
  env_disinfection_commission_settlement: {
    code: "env_disinfection_commission_settlement",
    name: "수수료 정산",
    intro: (v) => (v.vendor ? `${v.vendor} 수수료를 아래와 같이 정산하고자 합니다.` : ""),
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "정산금액", type: "money" },
      { key: "vendor", label: "거래처명", type: "text" },
      {
        key: "detail",
        label: "정산내역",
        type: "text",
        input: true,
        default: "(정산 용도)수수료 합계",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "환경사업, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n공공운영비, 지급수수료",
      },
    ],
    attachments: [{ name: "정산내역서", count: 1 }],
  },

  // ✅ 환경/소독 전용(신규, 임시안) — 차량정비비. 실사용 5건(브레이크등교체/타이어교체/
  // 자동차보험료/세차비/통행료 등 차량 관련 잡비)을 하나로 묶음 — 붙임 구성은 실물과 대조 필요.
  env_disinfection_vehicle_maintenance: {
    code: "env_disinfection_vehicle_maintenance",
    name: "차량정비비",
    intro: (v) => (v.vendor ? `업무용 차량 정비비를 ${v.vendor}에 아래와 같이 지출하고자 합니다.` : ""),
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      { key: "vendor", label: "업 체 명", type: "text" },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        placeholder: "예: 브레이크등 교체, 타이어 교체 등",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "환경사업, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n공공운영비, 차량선박비, 차량유지비",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
  },

  // ✅ 카페인 가산점 전용 — 재료구매
  gasan_ingredient: {
    code: "gasan_ingredient",
    name: "재료구매",
    intro: (v) =>
      v.month
        ? `카페인 가산점 ${v.month} 음료 판매를 위한 재료를 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "원두 및 음료재료, 포장재료",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "㈜포스트로스터리 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 가산점, 사업비용, 영업비용, 판매비와관리비, 물건비, 재료비, 일반재료비",
      },
    ],
    attachments: [{ name: "세금계산서, 거래명세서 외", count: 1 }],
  },

  // ✅ 카페인 가산점 전용 — 상품구매
  gasan_goods: {
    code: "gasan_goods",
    name: "상품구매",
    intro: (v) =>
      v.month
        ? `카페인 가산점 ${v.month} 판매 상품을 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "음료 상품, 스낵(디저트) 상품",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "쿠팡 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 가산점, 사업비용, 영업비용, 판매비와관리비, 물건비, 상품매입비",
      },
    ],
    attachments: [{ name: "세금계산서, 영수증 외", count: 1 }],
  },

  // ✅ 카페인 가산점 전용 — 소모품구매 (경영팀 소모품구매(supplies)와 별개 템플릿)
  gasan_supplies: {
    code: "gasan_supplies",
    name: "소모품구매",
    intro: (v) =>
      v.month
        ? `카페인 가산점 ${v.month} 판매를 위한 소모품을 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "종량제봉투 및 장갑 외",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "쿠팡 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 가산점, 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 사무관리비, 일반수용비, 소모품비",
      },
    ],
    attachments: [{ name: "세금계산서, 카드매출전표 외", count: 1 }],
  },

  // ✅ 카페인 가산점 전용 — 포스임대료 (귀속연월 없음, 매달 반복되는 고정형)
  gasan_pos_rental: {
    code: "gasan_pos_rental",
    name: "포스임대료",
    intro: "카페인 가산점 포스 임대료를 아래와 같이 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "㈜스카이티제이",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "공급가액", "부가세", "총계", "비고"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인가산점, 사업비용, 영업비용, 판매비와일반관리비, 물건비, 일반운영비, 사무관리비, 임차료, 포스임차료",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }],
  },

  // ✅ 카페인 가산점 전용 — 인터넷·전화요금 (경영팀 인터넷·전화요금(utility_fee)과 별개)
  gasan_internet: {
    code: "gasan_internet",
    name: "인터넷·전화요금",
    intro: (v) =>
      v.month
        ? `카페인 가산점 ${v.month} 인터넷 및 전화 요금을 아래와 같이 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "SK 브로드밴드(주)",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "매월 25일 자동이체",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인가산점, 사업비용, 영업비용, 판매비와일반관리비, 물건비, 일반운영비, 공공운영비, 공공요금및제세",
      },
    ],
    attachments: [{ name: "청구서", count: 1 }],
  },

  // ✅ 카페인 가산점 전용 — 신용카드결제 (경영팀 신용카드(credit_card)와 별개 detailOption)
  gasan_credit_card: {
    code: "gasan_credit_card",
    name: "신용카드결제",
    intro: (v) =>
      v.month
        ? `카페인 가산점 신용카드(${v.cardNo || ""})의 ${v.month} 결제대금을 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "우리카드",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내      역", "일시불", "연회비", "총      계", "비      고"],
      },
    ],
    attachments: [{ name: "우리카드 이용대금명세서", count: 1 }],
  },

  // ✅ 카페인 가산점 전용 — 급여지급 (경영팀 임직원급여(payroll)와 별개 detailOption)
  gasan_payroll: {
    code: "gasan_payroll",
    name: "급여지급",
    intro: (v) =>
      v.month ? `${v.month} 카페인(CAFE_INN) 가산점 급여를 다음과 같이 지급하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      { key: "payDate", label: "지  급  일", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지정계좌이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임 급여내역서 참고",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 가산점, 사업비용, 영업비용, 판매비와관리비, 인건비, 기간제근로자등보수",
      },
    ],
    attachments: [{ name: "급여내역서", count: 1 }],
  },

  // ✅ 카페인 가산점 전용 — 사회보험료
  gasan_social_insurance: {
    code: "gasan_social_insurance",
    name: "사회보험료",
    intro: (v) =>
      v.month ? `카페인(CAFE_INN) 가산점 ${v.month}분 사회보험료를 다음과 같이 납부하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지로납부_지정계좌이체 (우리은행 457-840272-18-176)",
      },
      {
        key: "note",
        label: "세부내역",
        type: "text",
        input: true,
        default: "※붙임1. 2. 참고",
      },
      {
        key: "detail",
        label: "사회보험료 내역",
        type: "table",
        columns: ["내역", "구분", "성명", "건강보험", "장기요양", "국민연금", "고용보험", "산재보험", "총계"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "사업비용, 영업비용, 판매비와관리비, 물건비, 연금부담금 등, 사회보험부담금, 카페인 가산점",
      },
    ],
    attachments: [
      { name: "납부고지서", count: 1 },
      { name: "개인별산출내역", count: 4 },
    ],
  },

  // ✅ 카페인 독산점 전용 — 재료구매 (가산점과 완전 동일, "가산점"→"독산점"만 교체)
  doksan_ingredient: {
    code: "doksan_ingredient",
    name: "재료구매",
    intro: (v) =>
      v.month
        ? `카페인 독산점 ${v.month} 음료 판매를 위한 재료를 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "원두 및 음료재료, 포장재료",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "㈜포스트로스터리 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 독산점, 사업비용, 영업비용, 판매비와관리비, 물건비, 재료비, 일반재료비",
      },
    ],
    attachments: [{ name: "세금계산서, 거래명세서 외", count: 1 }],
  },

  // ✅ 카페인 독산점 전용 — 상품구매 (가산점과 완전 동일, "가산점"→"독산점"만 교체)
  doksan_goods: {
    code: "doksan_goods",
    name: "상품구매",
    intro: (v) =>
      v.month
        ? `카페인 독산점 ${v.month} 판매 상품을 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "음료 상품, 스낵(디저트) 상품",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "쿠팡 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 독산점, 사업비용, 영업비용, 판매비와관리비, 물건비, 상품매입비",
      },
    ],
    attachments: [{ name: "세금계산서, 영수증 외", count: 1 }],
  },

  // ✅ 카페인 독산점 전용 — 포스임대료 (가산점과 완전 동일, "가산점"→"독산점"만 교체.
  // 귀속연월 없음(매달 반복되는 고정형). 표 계산 로직은 TemplateFormFields.jsx/DocumentPreview.jsx의
  // gasan_pos_rental 조건을 그대로 재사용(templateState.posSupply/posNote 공유 — 값도 동일해 문제 없음))
  doksan_pos_rental: {
    code: "doksan_pos_rental",
    name: "포스임대료",
    intro: "카페인 독산점 포스 임대료를 아래와 같이 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "㈜스카이티제이",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "공급가액", "부가세", "총계", "비고"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인독산점, 사업비용, 영업비용, 판매비와일반관리비, 물건비, 일반운영비, 사무관리비, 임차료, 포스임차료",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }],
  },

  // ✅ 카페인 독산점 전용 — 급여지급 (가산점과 완전 동일, "가산점"→"독산점"만 교체)
  doksan_payroll: {
    code: "doksan_payroll",
    name: "급여지급",
    intro: (v) =>
      v.month ? `${v.month} 카페인(CAFE_INN) 독산점 급여를 다음과 같이 지급하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      { key: "payDate", label: "지  급  일", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지정계좌이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임 급여내역서 참고",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 독산점, 사업비용, 영업비용, 판매비와관리비, 인건비, 기간제근로자등보수",
      },
    ],
    attachments: [{ name: "급여내역서", count: 1 }],
  },

  // ✅ 카페인 독산점 전용 — 사회보험료 (가산점과 완전 동일, "가산점"→"독산점"만 교체.
  // 지출방법의 계좌번호는 원래 "가산점"을 포함하지 않아 그대로 재사용됨)
  doksan_social_insurance: {
    code: "doksan_social_insurance",
    name: "사회보험료",
    intro: (v) =>
      v.month ? `카페인(CAFE_INN) 독산점 ${v.month}분 사회보험료를 다음과 같이 납부하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지로납부_지정계좌이체 (우리은행 457-840272-18-176)",
      },
      {
        key: "note",
        label: "세부내역",
        type: "text",
        input: true,
        default: "※붙임1. 2. 참고",
      },
      {
        key: "detail",
        label: "사회보험료 내역",
        type: "table",
        columns: ["내역", "구분", "성명", "건강보험", "장기요양", "국민연금", "고용보험", "산재보험", "총계"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "사업비용, 영업비용, 판매비와관리비, 물건비, 연금부담금 등, 사회보험부담금, 카페인 독산점",
      },
    ],
    attachments: [
      { name: "납부고지서", count: 1 },
      { name: "개인별산출내역", count: 4 },
    ],
  },

  // ✅ 카페인 독산점 전용 — 신용카드결제 (가산점과 완전 동일, 카드번호만 교체.
  // 카드번호는 templateState.gasanCardNo를 그대로 공유하되 detailOption별 기본값 useEffect로 구분)
  doksan_credit_card: {
    code: "doksan_credit_card",
    name: "신용카드결제",
    intro: (v) =>
      v.month
        ? `카페인 독산점 신용카드(${v.cardNo || ""})의 ${v.month} 결제대금을 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "우리카드",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내      역", "일시불", "연회비", "총      계", "비      고"],
      },
    ],
    attachments: [{ name: "우리카드 이용대금명세서", count: 1 }],
  },

  // ✅ 카페인 독산점 전용 — 인터넷·전화요금 (가산점 구조 재사용, 업체/지출방법/예산과목 실제 값 반영.
  // 건명·소개문 모두 "통신" 포함 — 카페독산지출-260622-003호 실기안 문구 반영)
  doksan_internet: {
    code: "doksan_internet",
    name: "인터넷·전화요금",
    intro: (v) =>
      v.month
        ? `카페인 독산점 ${v.month} 인터넷 및 전화 통신 요금을 아래와 같이 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "KT ㈜케이티",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "전용계좌납부(매월 22일 자동이체)",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인독산점, 사업비용, 영업비용, 판매비와일반관리비, 물건비, 일반운영비, 공공운영비, 공공요금 및 제세",
      },
    ],
    attachments: [{ name: "청구서", count: 1 }],
  },

  // ✅ 카페인 독산점 전용 신규 — 명절격려품 (바리스타 복리후생비)
  doksan_holiday_gift: {
    code: "doksan_holiday_gift",
    name: "명절격려품",
    intro: "카페인 독산점 바리스타 복리후생비(명절격려품)를 지출하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "vendor", label: "업 체 명", type: "text", input: true },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["지급대상", "상품명", "수량", "공급가액", "부가세", "소계"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 독산점, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비, 복리후생비, 명절격려품",
      },
    ],
    attachments: [
      { name: "세금계산서", count: 1 },
      { name: "견적서", count: 1 },
    ],
  },

  // ✅ 카페인 독산점 전용 신규 — 근로소득 원천세 (경영팀 withholding_tax와 동일 구조, state 공유)
  doksan_withholding_tax: {
    code: "doksan_withholding_tax",
    name: "원천세",
    intro: "금천일자리주식회사 카페인(CAFE_INN) 독산점 근로소득 원천세를 아래와 같이 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      { key: "dueDate", label: "납부기한", type: "text" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "성명", "근로소득세", "지방소득세", "총계"],
      },
    ],
    attachments: [
      { name: "원천징수이행상황신고서", count: 1 },
      { name: "납부서", count: 2 },
    ],
  },

  // ✅ 카페인 독산점 전용 신규 — 자판기 단말기 이용료
  doksan_vending_terminal: {
    code: "doksan_vending_terminal",
    name: "자판기단말기",
    intro: "카페인 독산점 자판기용 단말기 이용료를 아래와 같이 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "한동정보통신",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "매월 25일 자동이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "수량", "단가", "공급가액", "부가세", "합계"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인독산점, 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 사무관리비, 임차료, 지급임차료",
      },
    ],
    attachments: [{ name: "이체내역서", count: 1 }],
  },

  doksan_vat: {
    code: "doksan_vat",
    name: "부가세",
    intro: (v) =>
      v.year && v.quarter
        ? `CAFE_INN 독산점 ${v.year}년 ${v.quarter}기 예정고지 부가세를 아래와 같이 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "국세자진납부(계좌이체)",
      },
      { key: "dueDate", label: "납  기  일", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인독산점, 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 공공운영비, 부가세",
      },
    ],
    attachments: [{ name: "납부서", count: 1 }],
  },

  doksan_hygiene_edu: {
    code: "doksan_hygiene_edu",
    name: "위생교육",
    intro: (v) =>
      v.year ? `카페인 독산점 ${v.year}년 위생교육 이수 비용을 아래와 같이 결제하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      { key: "dueDate", label: "납  기  일", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 독산점 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 교육훈련비",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
  },

  // ✅ 카페인 독산점 공과금 3종(수도/가스/전기) — 필드 구성이 100% 동일하고 문구만 다르므로
  // 작은 팩토리 함수로 생성 (가산 7종 복제 때와 달리 이번엔 실익이 있다고 판단).
  ...(() => {
    // ✅ storeLabel 파라미터화 — 카페인 금나래점 공과금 3종도 동일 구조라 재사용(수도만 "{월}월" 특례,
    // code.endsWith("_water")로 매장 구분 없이 판별)
    const makeCafeUtilityTemplate = ({ code, name, storeLabel, feeLabel, method, attachments }) => ({
      code,
      name,
      intro: (v) => {
        if (!v.month) return "";
        if (code.endsWith("_water")) {
          const monthNum = (v.month.match(/(\d+)월/) || [])[1] || "";
          return monthNum ? `카페인 ${storeLabel} ${monthNum}월 ${feeLabel}${getEulReul(feeLabel)} 아래와 같이 납부하고자 합니다.` : "";
        }
        return `카페인 ${storeLabel} ${v.month}분 ${feeLabel}${getEulReul(feeLabel)} 아래와 같이 납부하고자 합니다.`;
      },
      fields: [
        { key: "title", label: "건      명", type: "text" },
        { key: "amount", label: "소요예산", type: "money" },
        {
          key: "method",
          label: "지출방법",
          type: "text",
          input: true,
          default: method,
        },
        { key: "usagePeriod", label: "사용기간", type: "text" },
        { key: "dueDate", label: "납  기  일", type: "text" },
        {
          key: "budgetItem",
          label: "예산과목",
          type: "text",
          multiline: true,
          input: true,
          default: `카페인 ${storeLabel}, 사업비용, 영업비용, 판매비와관리비, 일반운영비, 공공요금 및 제세, ${feeLabel}`,
        },
      ],
      attachments,
    });

    return {
      doksan_water: makeCafeUtilityTemplate({
        code: "doksan_water",
        name: "수도",
        storeLabel: "독산점",
        feeLabel: "수도요금",
        method: "전용계좌이체",
        attachments: [
          { name: "수도요금 청구서", count: 1 },
          { name: "수도요금 정산내역서", count: 1 },
        ],
      }),
      doksan_gas: makeCafeUtilityTemplate({
        code: "doksan_gas",
        name: "가스",
        storeLabel: "독산점",
        feeLabel: "가스요금",
        method: "전용계좌이체",
        attachments: [
          { name: "가스요금 청구서", count: 1 },
          { name: "가스요금 정산내역서", count: 1 },
        ],
      }),
      doksan_electric: makeCafeUtilityTemplate({
        code: "doksan_electric",
        name: "전기",
        storeLabel: "독산점",
        feeLabel: "전기료",
        method: "전용계좌이체",
        attachments: [{ name: "전기요금 청구서", count: 1 }],
      }),
      geumnarae_water: makeCafeUtilityTemplate({
        code: "geumnarae_water",
        name: "수도",
        storeLabel: "금나래점",
        feeLabel: "수도요금",
        method: "전용계좌이체",
        attachments: [
          { name: "수도요금 청구서", count: 1 },
          { name: "수도요금 정산내역서", count: 1 },
        ],
      }),
      geumnarae_gas: makeCafeUtilityTemplate({
        code: "geumnarae_gas",
        name: "가스",
        storeLabel: "금나래점",
        feeLabel: "가스요금",
        method: "전용계좌이체",
        attachments: [
          { name: "가스요금 청구서", count: 1 },
          { name: "가스요금 정산내역서", count: 1 },
        ],
      }),
      geumnarae_electric: makeCafeUtilityTemplate({
        code: "geumnarae_electric",
        name: "전기",
        storeLabel: "금나래점",
        feeLabel: "전기료",
        method: "전용계좌이체",
        attachments: [{ name: "전기요금 청구서", count: 1 }],
      }),
      green_water: makeCafeUtilityTemplate({
        code: "green_water",
        name: "수도",
        storeLabel: "그린점",
        feeLabel: "수도요금",
        method: "전용계좌이체",
        attachments: [
          { name: "수도요금 청구서", count: 1 },
          { name: "수도요금 정산내역서", count: 1 },
        ],
      }),
      green_gas: makeCafeUtilityTemplate({
        code: "green_gas",
        name: "가스",
        storeLabel: "그린점",
        feeLabel: "가스요금",
        method: "전용계좌이체",
        attachments: [
          { name: "가스요금 청구서", count: 1 },
          { name: "가스요금 정산내역서", count: 1 },
        ],
      }),
      green_electric: makeCafeUtilityTemplate({
        code: "green_electric",
        name: "전기",
        storeLabel: "그린점",
        feeLabel: "전기료",
        method: "전용계좌이체",
        attachments: [{ name: "전기요금 청구서", count: 1 }],
      }),
    };
  })(),

  // ✅ 카페인 금나래점 전용 — 재료구매 (가산점과 완전 동일, "가산점"→"금나래점"만 교체)
  geumnarae_ingredient: {
    code: "geumnarae_ingredient",
    name: "재료구매",
    intro: (v) =>
      v.month
        ? `카페인 금나래점 ${v.month} 음료 판매를 위한 재료를 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "원두 및 음료재료, 포장재료",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "㈜포스트로스터리 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 금나래점, 사업비용, 영업비용, 판매비와관리비, 물건비, 재료비, 일반재료비",
      },
    ],
    attachments: [{ name: "세금계산서, 거래명세서 외", count: 1 }],
  },

  // ✅ 카페인 금나래점 전용 — 상품구매 (가산점과 완전 동일, "가산점"→"금나래점"만 교체)
  geumnarae_goods: {
    code: "geumnarae_goods",
    name: "상품구매",
    intro: (v) =>
      v.month
        ? `카페인 금나래점 ${v.month} 판매 상품을 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "음료 상품, 스낵(디저트) 상품",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "쿠팡 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 금나래점, 사업비용, 영업비용, 판매비와관리비, 물건비, 상품매입비",
      },
    ],
    attachments: [{ name: "세금계산서, 영수증 외", count: 1 }],
  },

  // ✅ 카페인 금나래점 전용 — 소모품구매 (가산점과 완전 동일, "가산점"→"금나래점"만 교체)
  geumnarae_supplies: {
    code: "geumnarae_supplies",
    name: "소모품구매",
    intro: (v) =>
      v.month
        ? `카페인 금나래점 ${v.month} 판매를 위한 소모품을 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "종량제봉투 및 장갑 외",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "쿠팡 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 금나래점, 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 사무관리비, 일반수용비, 소모품비",
      },
    ],
    attachments: [{ name: "세금계산서, 카드매출전표 외", count: 1 }],
  },

  // ✅ 카페인 금나래점 전용 — 급여지급 (가산점과 완전 동일, "가산점"→"금나래점"만 교체)
  geumnarae_payroll: {
    code: "geumnarae_payroll",
    name: "급여지급",
    intro: (v) =>
      v.month ? `${v.month} 카페인(CAFE_INN) 금나래점 급여를 다음과 같이 지급하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      { key: "payDate", label: "지  급  일", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지정계좌이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임 급여내역서 참고",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 금나래점, 사업비용, 영업비용, 판매비와관리비, 인건비, 기간제근로자등보수",
      },
    ],
    attachments: [{ name: "급여내역서", count: 1 }],
  },

  // ✅ 카페인 금나래점 전용 — 사회보험료 (가산점과 완전 동일 구조, 계좌번호만 실제 값 반영)
  geumnarae_social_insurance: {
    code: "geumnarae_social_insurance",
    name: "사회보험료",
    intro: (v) =>
      v.month ? `카페인(CAFE_INN) 금나래점 ${v.month}분 사회보험료를 다음과 같이 납부하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지로납부_지정계좌이체 (우리은행 457-165478-18-089)",
      },
      {
        key: "note",
        label: "세부내역",
        type: "text",
        input: true,
        default: "※붙임1. 2. 참고",
      },
      {
        key: "detail",
        label: "사회보험료 내역",
        type: "table",
        columns: ["내역", "구분", "성명", "건강보험", "장기요양", "국민연금", "고용보험", "산재보험", "총계"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "사업비용, 영업비용, 판매비와관리비, 물건비, 연금부담금 등, 사회보험부담금, 카페인 금나래점",
      },
    ],
    attachments: [
      { name: "납부고지서", count: 1 },
      { name: "개인별산출내역", count: 4 },
    ],
  },

  // ✅ 카페인 금나래점 전용 — 포스임대료 (가산점과 완전 동일 구조, "가산점"→"금나래점"만 교체.
  // 귀속연월 없음(매달 반복되는 고정형). 표 계산 로직은 gasan_pos_rental 조건 재사용 —
  // 공급가액/비고 기본값(100,000원/매월 5일 자동이체)은 posSupply 기본값 useEffect로 처리)
  geumnarae_pos_rental: {
    code: "geumnarae_pos_rental",
    name: "포스임대료",
    intro: "카페인 금나래점 포스 임대료를 아래와 같이 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "㈜스카이티제이",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "공급가액", "부가세", "총계", "비고"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인금나래점, 사업비용, 영업비용, 판매비와일반관리비, 물건비, 일반운영비, 사무관리비, 임차료, 포스임차료",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }],
  },

  // ✅ 카페인 금나래점 전용 — 신용카드결제 (가산점과 완전 동일, 카드번호만 교체.
  // 카드번호/비고는 templateState.gasanCardNo/gasanCardNote를 공유하되 detailOption별 기본값 useEffect로 구분)
  geumnarae_credit_card: {
    code: "geumnarae_credit_card",
    name: "신용카드결제",
    intro: (v) =>
      v.month
        ? `카페인 금나래점 신용카드(${v.cardNo || ""})의 ${v.month} 결제대금을 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "우리카드",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내      역", "일시불", "연회비", "총      계", "비      고"],
      },
    ],
    attachments: [{ name: "우리카드 이용대금명세서", count: 1 }],
  },

  // ✅ 카페인 금나래점 전용 — 인터넷·전화요금 (가산점이 아닌 독산점 KT형 구조 재사용,
  // 업체/지출방법/예산과목은 금나래 실제 값 반영)
  geumnarae_internet: {
    code: "geumnarae_internet",
    name: "인터넷·전화요금",
    intro: (v) =>
      v.month
        ? `카페인 금나래점 ${v.month} 인터넷 및 전화 통신 요금을 아래와 같이 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "KT ㈜케이티",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "전용계좌납부",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인금나래점, 사업비용, 영업비용, 판매비와일반관리비, 물건비, 일반운영비, 공공운영비, 공공요금 및 제세",
      },
    ],
    attachments: [{ name: "청구서", count: 1 }],
  },

  // ✅ 카페인 금나래점 전용 신규 — 근로소득 원천세 (doksan_withholding_tax와 완전 동일 구조/state 재사용)
  geumnarae_withholding_tax: {
    code: "geumnarae_withholding_tax",
    name: "원천세",
    intro: "금천일자리주식회사 카페인(CAFE_INN) 금나래점 근로소득 원천세를 아래와 같이 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      { key: "dueDate", label: "납부기한", type: "text" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "성명", "근로소득세", "지방소득세", "총계"],
      },
    ],
    attachments: [
      { name: "원천징수이행상황신고서", count: 1 },
      { name: "납부서", count: 2 },
    ],
  },

  // ✅ 카페인 금나래점 전용 신규 — 명절격려품 (doksan_holiday_gift와 완전 동일 구조/state 재사용)
  geumnarae_holiday_gift: {
    code: "geumnarae_holiday_gift",
    name: "명절격려품",
    intro: "카페인 금나래점 바리스타 복리후생비(명절격려품)를 지출하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "vendor", label: "업 체 명", type: "text", input: true },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["지급대상", "상품명", "수량", "공급가액", "부가세", "소계"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 금나래점, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비, 복리후생비, 명절격려품",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }, { name: "견적서", count: 1 }],
  },

  // ✅ 카페인 금나래점 전용 — 부가세(예정고지/확정 겸용). 독산과 달리 "예정고지"가 고정이 아니라
  // 건별로 다를 수 있어(확정 부가세도 있음) vatType을 사용자 입력 필드로 둠
  geumnarae_vat: {
    code: "geumnarae_vat",
    name: "부가세",
    intro: (v) =>
      v.year && v.quarter && v.vatType
        ? `CAFE_INN 금나래점 ${v.year}년 ${v.quarter}기 ${v.vatType} 부가세를 아래와 같이 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "국세자진납부(계좌이체)",
      },
      { key: "dueDate", label: "납  기  일", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인금나래점, 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 공공운영비, 부가세",
      },
    ],
    attachments: [{ name: "납부서", count: 1 }],
  },

  // ✅ 카페인 가산점 전용 — 관리비
  gasan_maintenance: {
    code: "gasan_maintenance",
    name: "관리비",
    intro: (v) =>
      v.month ? `카페인 가산점 ${v.month} 관리비를 아래와 같이 납부하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      { key: "dueDate", label: "납  기  일", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 가산점 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 사무관리비 임차료, 사업장 임차료",
      },
    ],
    attachments: [
      { name: "청구서", count: 1 },
      { name: "세금계산서", count: 1 },
    ],
  },

  meal_allowance: {
    code: "meal_allowance",
    name: "급량비",
    intro:
      "금천일자리주식회사 시간외근무에 따른 직원 급량비를 아래와 같이 지출하여 보고드립니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "datetime",
        label: "일      시",
        type: "text",
        input: true,
        placeholder: "예: 2026. 5. 11.(월) 18:00",
      },
      {
        key: "place",
        label: "장      소",
        type: "text",
        input: true,
        placeholder: "예: 도원각 (금천구 범안로 1245, 1층)",
      },
      {
        key: "target",
        label: "집행대상",
        type: "text",
        input: true,
        placeholder: "예: 직원 3명",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "체크카드결제",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 일반운영비,\n복리후생비, 급량비",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
  },

  interview_fee: {
    code: "interview_fee",
    name: "채용심사비",
    intro:
      "금천일자리주식회사 현장근로자 채용과 관련하여 아래와 같이 서류 및 면접심사비를 지급하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내용", "산출내역", "지급액", "소득세", "실지급액"],
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와일반관리비, 일반운영비,\n운영수당, 심사수당",
      },
    ],
    attachments: [
      { name: "심사비 지급확인서 및 개인정보 수집 이용 동의서", count: 3 },
    ],
  },

  car_installment: {
    code: "car_installment",
    name: "차량할부금",
    intro:
      "금천일자리주식회사 업무용 특장 차량(871나 6483) 할부금을 아래와 같이 지출하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "vendor",
        label: "업      체",
        type: "text",
        input: true,
        default: "기아(주)_현대캐피탈",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "자동이체(매월 25일)",
      },
      {
        key: "contractInfo",
        label: "계약정보",
        type: "table",
        showUnit: false,
        infoTable: true,
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "본사, 사업비용, 영업비용, 판매비와 일반관리비, 자산취득비",
      },
    ],
    attachments: [
      { name: "차량견적서", count: 1 },
      { name: "세금계산서", count: 1 },
      { name: "계좌이체확인증", count: 1 },
    ],
  },

  deduction_refund: {
    code: "deduction_refund",
    name: "공제금환급",
    intro:
      "금천일자리주식회사 현장근로자 일용직의 공제금을 다음과 같이 환급하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "target", label: "대 상 자", type: "text" },
      { key: "amount", label: "환급금액", type: "money" },
      {
        key: "method",
        label: "환급방법",
        type: "text",
        input: true,
        default: "예수금 통장에서 본인계좌입금",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["성명", "고용보험 본인부담금", "비고"],
      },
    ],
    attachments: [{ name: "일용직 현장근로자 급여명세서", count: 1 }],
  },

  pension: {
    code: "pension",
    name: "퇴직연금",
    intro: (v) =>
      v.month ? `${v.month}분 퇴직연금을 다음과 같이 납부하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      { key: "dueDate", label: "납부기한", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지정계좌이체(우리은행/1121-201-783453)",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: [{ label: "내역", colSpan: 2 }, "금액", "합계"],
        deptTable: true,
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "사업비용, 영업비용, 판매비와관리비, 인건비, 퇴직급여, 퇴직연금",
      },
    ],
    attachments: [{ name: "퇴직연금 부담금 명세서", count: 1 }],
  },

  withholding_tax: {
    code: "withholding_tax",
    name: "원천세",
    intro: "금천일자리주식회사 근로소득 원천세를 아래와 같이 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      { key: "dueDate", label: "납부기한", type: "text" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "성명", "근로소득세", "지방소득세", "총계"],
      },
    ],
    attachments: [
      { name: "원천징수이행상황신고서", count: 1 },
      { name: "납부서", count: 2 },
    ],
  },

  water_rental: {
    code: "water_rental",
    name: "정수기렌탈료",
    intro: (v) =>
      v.month
        ? `금천일자리주식회사 ${v.month} 사무실 정수기 렌탈료를 아래와 같이 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "남부상사",
      },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체(국민은행, 075601-04-302640)",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "공급가액", "부가세", "총계", "비고"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비,\n사무관리비, 임차료, 지급임차료, 정수기 렌탈료",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }],
  },

  server_fee: {
    code: "server_fee",
    name: "서버이용료",
    intro: "금천일자리주식회사 홈페이지 서버이용료를 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "㈜세진컴퍼니",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "금액", "부가세", "총계", "비고"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default:
          "본사, 사업비용, 영업비용, 판매비와 일반관리비, 물건비,\n일반운영비, 사무관리비, 일반수용비",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 — 재료구매 (geumnarae_ingredient 기반, "금나래점"→"그린점"만 교체)
  green_ingredient: {
    code: "green_ingredient",
    name: "재료구매",
    intro: (v) =>
      v.month
        ? `카페인 그린점 ${v.month} 음료 판매를 위한 재료를 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "원두 및 음료재료, 포장재료",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "㈜포스트로스터리 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 그린점, 사업비용, 영업비용, 판매비와관리비, 물건비, 재료비, 일반재료비",
      },
    ],
    attachments: [{ name: "세금계산서, 거래명세서 외", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 — 상품구매 (geumnarae_goods 기반, "금나래점"→"그린점"만 교체)
  green_goods: {
    code: "green_goods",
    name: "상품구매",
    intro: (v) =>
      v.month
        ? `카페인 그린점 ${v.month} 판매 상품을 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "음료 상품, 스낵(디저트) 상품",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "쿠팡 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 그린점, 사업비용, 영업비용, 판매비와관리비, 물건비, 상품매입비",
      },
    ],
    attachments: [{ name: "세금계산서, 영수증 외", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 — 소모품구매 (geumnarae_supplies 기반, "금나래점"→"그린점"만 교체)
  green_supplies: {
    code: "green_supplies",
    name: "소모품구매",
    intro: (v) =>
      v.month
        ? `카페인 그린점 ${v.month} 판매를 위한 소모품을 아래와 같이 구매하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "detail",
        label: "적      요",
        type: "text",
        input: true,
        default: "종량제봉투 및 장갑 외",
      },
      {
        key: "vendor",
        label: "구매업체",
        type: "text",
        input: true,
        default: "쿠팡 외",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 그린점, 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 사무관리비, 일반수용비, 소모품비",
      },
    ],
    attachments: [{ name: "세금계산서, 카드매출전표 외", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 — 포스임대료 (geumnarae_pos_rental 기반이나, 그린점은 신규 개점이라
  // 공급가액/비고 기본값 useEffect는 두지 않음 — 업체 기본값만 3개점 공통값 "㈜스카이티제이" 유지)
  green_pos_rental: {
    code: "green_pos_rental",
    name: "포스임대료",
    intro: "카페인 그린점 포스 임대료를 아래와 같이 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "vendor",
        label: "업 체 명",
        type: "text",
        input: true,
        default: "㈜스카이티제이",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "공급가액", "부가세", "총계", "비고"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인그린점, 사업비용, 영업비용, 판매비와일반관리비, 물건비, 일반운영비, 사무관리비, 임차료, 포스임차료",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 — 인터넷·전화요금 (geumnarae_internet 기반이나, 그린점은 신규 개점이라
  // 업체/지출방법 기본값 없이 사용자 입력으로 둠)
  green_internet: {
    code: "green_internet",
    name: "인터넷·전화요금",
    intro: (v) =>
      v.month
        ? `카페인 그린점 ${v.month} 인터넷 및 전화 통신 요금을 아래와 같이 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "vendor", label: "업 체 명", type: "text", input: true },
      { key: "method", label: "지출방법", type: "text", input: true },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인그린점, 사업비용, 영업비용, 판매비와일반관리비, 물건비, 일반운영비, 공공운영비, 공공요금 및 제세",
      },
    ],
    attachments: [{ name: "청구서", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 — 신용카드결제 (geumnarae_credit_card 기반이나, 그린점은 신규 개점이라
  // 카드사 기본값 없이 사용자 입력으로 둠. 카드번호도 gasanCardNo 기본값 useEffect 대상에서 제외)
  green_credit_card: {
    code: "green_credit_card",
    name: "신용카드결제",
    intro: (v) =>
      v.month
        ? `카페인 그린점 신용카드(${v.cardNo || ""})의 ${v.month} 결제대금을 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      { key: "vendor", label: "업 체 명", type: "text", input: true },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내      역", "일시불", "연회비", "총      계", "비      고"],
      },
    ],
    attachments: [{ name: "이용대금명세서", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 — 급여지급 (geumnarae_payroll 기반, "금나래점"→"그린점"만 교체)
  green_payroll: {
    code: "green_payroll",
    name: "급여지급",
    intro: (v) =>
      v.month ? `${v.month} 카페인(CAFE_INN) 그린점 급여를 다음과 같이 지급하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      { key: "payDate", label: "지  급  일", type: "text" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지정계좌이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "text",
        input: true,
        default: "붙임 급여내역서 참고",
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 그린점, 사업비용, 영업비용, 판매비와관리비, 인건비, 기간제근로자등보수",
      },
    ],
    attachments: [{ name: "급여내역서", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 — 사회보험료 (geumnarae_social_insurance 기반이나, 그린 개설 계좌 미확인이라
  // 지출방법 기본값에서 계좌번호는 비워둠)
  green_social_insurance: {
    code: "green_social_insurance",
    name: "사회보험료",
    intro: (v) =>
      v.month ? `카페인(CAFE_INN) 그린점 ${v.month}분 사회보험료를 다음과 같이 납부하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "지출금액", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "지로납부_지정계좌이체",
      },
      {
        key: "note",
        label: "세부내역",
        type: "text",
        input: true,
        default: "※붙임1. 2. 참고",
      },
      {
        key: "detail",
        label: "사회보험료 내역",
        type: "table",
        columns: ["내역", "구분", "성명", "건강보험", "장기요양", "국민연금", "고용보험", "산재보험", "총계"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "사업비용, 영업비용, 판매비와관리비, 물건비, 연금부담금 등, 사회보험부담금, 카페인 그린점",
      },
    ],
    attachments: [
      { name: "납부고지서", count: 1 },
      { name: "개인별산출내역", count: 4 },
    ],
  },

  // ✅ 카페인 그린점 전용 — 관리비 (gasan_maintenance 기반, "가산점"→"그린점"만 교체.
  // 금액·내역은 원래도 고정 기본값이 없어 그대로 사용자 입력)
  green_maintenance: {
    code: "green_maintenance",
    name: "관리비",
    intro: (v) =>
      v.month ? `카페인 그린점 ${v.month} 관리비를 아래와 같이 납부하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      { key: "dueDate", label: "납  기  일", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 그린점 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 사무관리비 임차료, 사업장 임차료",
      },
    ],
    attachments: [
      { name: "청구서", count: 1 },
      { name: "세금계산서", count: 1 },
    ],
  },

  // ✅ 카페인 그린점 전용 신규 — 명절격려품 (geumnarae_holiday_gift 기반, "금나래점"→"그린점"만 교체)
  green_holiday_gift: {
    code: "green_holiday_gift",
    name: "명절격려품",
    intro: "카페인 그린점 바리스타 복리후생비(명절격려품)를 지출하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      { key: "vendor", label: "업 체 명", type: "text", input: true },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "계좌이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["지급대상", "상품명", "수량", "공급가액", "부가세", "소계"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 그린점, 사업비용, 영업비용, 판매비와 일반관리비, 물건비, 일반운영비, 복리후생비, 명절격려품",
      },
    ],
    attachments: [{ name: "세금계산서", count: 1 }, { name: "견적서", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 신규 — 근로소득 원천세 (geumnarae_withholding_tax와 완전 동일 구조/state 재사용)
  green_withholding_tax: {
    code: "green_withholding_tax",
    name: "원천세",
    intro: "금천일자리주식회사 카페인(CAFE_INN) 그린점 근로소득 원천세를 아래와 같이 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      { key: "dueDate", label: "납부기한", type: "text" },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "성명", "근로소득세", "지방소득세", "총계"],
      },
    ],
    attachments: [
      { name: "원천징수이행상황신고서", count: 1 },
      { name: "납부서", count: 2 },
    ],
  },

  // ✅ 카페인 그린점 전용 신규 — 자판기단말기 (doksan_vending_terminal 기반이나, 그린점은 신규 개점이라
  // 업체 기본값 없이 사용자 입력으로 둠)
  green_vending_terminal: {
    code: "green_vending_terminal",
    name: "자판기단말기",
    intro: "카페인 그린점 자판기용 단말기 이용료를 아래와 같이 납부하고자 합니다.",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      { key: "vendor", label: "업 체 명", type: "text", input: true },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "매월 25일 자동이체",
      },
      {
        key: "detail",
        label: "세부내역",
        type: "table",
        columns: ["내역", "수량", "단가", "공급가액", "부가세", "합계"],
      },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인그린점, 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 사무관리비, 임차료, 지급임차료",
      },
    ],
    attachments: [{ name: "이체내역서", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 — 부가세(예정고지/확정 겸용, geumnarae_vat와 완전 동일 구조/state 재사용)
  green_vat: {
    code: "green_vat",
    name: "부가세",
    intro: (v) =>
      v.year && v.quarter && v.vatType
        ? `CAFE_INN 그린점 ${v.year}년 ${v.quarter}기 ${v.vatType} 부가세를 아래와 같이 납부하고자 합니다.`
        : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "금      액", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "국세자진납부(계좌이체)",
      },
      { key: "dueDate", label: "납  기  일", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인그린점, 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 공공운영비, 부가세",
      },
    ],
    attachments: [{ name: "납부서", count: 1 }],
  },

  // ✅ 카페인 그린점 전용 — 위생교육 (doksan_hygiene_edu 기반, "독산점"→"그린점"만 교체)
  green_hygiene_edu: {
    code: "green_hygiene_edu",
    name: "위생교육",
    intro: (v) =>
      v.year ? `카페인 그린점 ${v.year}년 위생교육 이수 비용을 아래와 같이 결제하고자 합니다.` : "",
    fields: [
      { key: "title", label: "건      명", type: "text" },
      { key: "amount", label: "소요예산", type: "money" },
      {
        key: "method",
        label: "지출방법",
        type: "text",
        input: true,
        default: "신용카드결제",
      },
      { key: "dueDate", label: "납  기  일", type: "text" },
      {
        key: "budgetItem",
        label: "예산과목",
        type: "text",
        multiline: true,
        input: true,
        default: "카페인 그린점 사업비용, 영업비용, 판매비와관리비, 물건비, 일반운영비, 교육훈련비",
      },
    ],
    attachments: [{ name: "영수증", count: 1 }],
  },
};